import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '@/config';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import PeopleIcon from '@mui/icons-material/People';
import PlaceIcon from '@mui/icons-material/Place';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StorageIcon from '@mui/icons-material/Storage';

interface AdminDashboardStats {
  totalUsers: number;
  totalPins: number;
  totalPhotos: number;
  newUsersLast7Days: number;
  storageMB: number;
}

interface AdminUser {
  id: string;
  email: string;
  name: string;
  plan: string;
  is_active: boolean;
  created_at: string;
}

interface AuditEntry {
  id: string;
  timestamp: string;
  admin_email: string;
  action: string;
  resource: string;
  details: string;
  ip: string;
}

interface ObservabilityData {
  system: {
    totalRequests: number;
    averageLatencyMs: number;
    p95LatencyMs: number;
    errorRate: number;
    requestsPerMinute: number;
    uptimeSeconds: number;
    dbStatus: string;
    dbLatencyMs: number;
    memory: { rss: number; heapUsed: number; heapTotal: number };
  };
  endpoints: { path: string; count: number; avgLatency: number; errorCount: number }[];
  requestsPerHour: { hour: string; count: number }[];
  recentErrors: { message: string; path: string; statusCode: number; timestamp: number }[];
  business: {
    pinsPerDay: { day: string; count: number }[];
    photosPerDay: { day: string; count: number }[];
    registrationsPerDay: { day: string; count: number }[];
    activeUsers24h: number;
    activeUsers7d: number;
  };
}

function getToken(): string {
  return localStorage.getItem('accessToken') || '';
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  return `${h}h ${m}m`;
}

export function AdminPage() {
  useTranslation();
  const [tab, setTab] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Dashboard state
  const [dashStats, setDashStats] = useState<AdminDashboardStats | null>(null);
  const [dashLoading, setDashLoading] = useState(false);

  // Users state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const [planDialog, setPlanDialog] = useState<{ open: boolean; userId: string; currentPlan: string }>({ open: false, userId: '', currentPlan: '' });
  const [newPlan, setNewPlan] = useState('');

  // Observability state
  const [obsData, setObsData] = useState<ObservabilityData | null>(null);
  const [obsLoading, setObsLoading] = useState(false);

  // Audit state
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [, setAuditTotal] = useState(0);

  const fetchDashboard = useCallback(async () => {
    setDashLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/admin/dashboard`, { headers: authHeaders() });
      if (res.status === 403) { setError('Acesso negado. Você não é administrador.'); return; }
      if (!res.ok) throw new Error('Erro ao carregar dashboard');
      const data = await res.json();
      setDashStats({
        totalUsers: data.totalUsers || 0,
        totalPins: data.totalPins || 0,
        totalPhotos: data.totalPhotos || 0,
        newUsersLast7Days: data.newUsersLast7Days || 0,
        storageMB: data.storageMB || 0,
      });
    } catch (e: any) {
      setError(e.message || 'Erro desconhecido');
    } finally {
      setDashLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async (query?: string) => {
    setUsersLoading(true);
    setError(null);
    try {
      const q = query !== undefined ? query : searchTerm;
      const url = q ? `${API_BASE_URL}/api/v1/admin/users?q=${encodeURIComponent(q)}` : `${API_BASE_URL}/api/v1/admin/users`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.status === 403) { setError('Acesso negado. Você não é administrador.'); return; }
      if (!res.ok) throw new Error('Erro ao carregar usuários');
      const data = await res.json();
      setUsers(data.users || data || []);
    } catch (e: any) {
      setError(e.message || 'Erro desconhecido');
    } finally {
      setUsersLoading(false);
    }
  }, [searchTerm]);

  const fetchObservability = useCallback(async () => {
    setObsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/admin/observability`, { headers: authHeaders() });
      if (res.status === 403) { setError('Acesso negado. Você não é administrador.'); return; }
      if (!res.ok) throw new Error('Erro ao carregar observabilidade');
      const data = await res.json();
      setObsData(data);
    } catch (e: any) {
      setError(e.message || 'Erro desconhecido');
    } finally {
      setObsLoading(false);
    }
  }, []);

  const fetchAuditLog = useCallback(async (page: number) => {
    setAuditLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/admin/audit-log?page=${page}&limit=20`, { headers: authHeaders() });
      if (res.status === 403) { setError('Acesso negado. Você não é administrador.'); return; }
      if (!res.ok) throw new Error('Erro ao carregar log de auditoria');
      const data = await res.json();
      setAuditLog(data.entries || data.logs || data.data || []);
      setAuditTotal(data.total || data.pagination?.total || 0);
    } catch (e: any) {
      setError(e.message || 'Erro desconhecido');
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 0) fetchDashboard();
    else if (tab === 1) fetchUsers();
    else if (tab === 2) fetchObservability();
    else if (tab === 3) fetchAuditLog(auditPage);
  }, [tab]);

  const handleUserAction = async (action: string, userId: string, body?: any) => {
    try {
      let url = '';
      let method = 'POST';
      switch (action) {
        case 'reset-password':
          url = `${API_BASE_URL}/api/v1/admin/users/${userId}/reset-password`;
          break;
        case 'unlock':
          url = `${API_BASE_URL}/api/v1/admin/users/${userId}/unlock`;
          break;
        case 'toggle-active':
          url = `${API_BASE_URL}/api/v1/admin/users/${userId}/toggle-active`;
          method = 'PATCH';
          break;
        case 'change-plan':
          url = `${API_BASE_URL}/api/v1/admin/users/${userId}/plan`;
          method = 'PATCH';
          break;
      }
      const res = await fetch(url, { method, headers: authHeaders(), body: body ? JSON.stringify(body) : undefined });
      if (!res.ok) throw new Error('Ação falhou');
      fetchUsers();
    } catch (e: any) {
      setError(e.message || 'Erro na ação');
    }
  };

  const openConfirm = (title: string, message: string, onConfirm: () => void) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  const statCards = dashStats ? [
    { label: 'Total Usuários', value: dashStats.totalUsers, icon: PeopleIcon, color: '#1565c0' },
    { label: 'Total Pins', value: dashStats.totalPins, icon: PlaceIcon, color: '#d32f2f' },
    { label: 'Total Fotos', value: dashStats.totalPhotos, icon: PhotoCameraIcon, color: '#42a5f5' },
    { label: 'Novos (7 dias)', value: dashStats.newUsersLast7Days, icon: TrendingUpIcon, color: '#f57c00' },
    { label: 'Armazenamento (MB)', value: dashStats.storageMB.toFixed(1), icon: StorageIcon, color: '#7b1fa2' },
  ] : [];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        Painel Administrativo
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Dashboard" />
        <Tab label="Usuários" />
        <Tab label="Observabilidade" />
        <Tab label="Auditoria" />
      </Tabs>

      {/* Tab 0: Dashboard */}
      {tab === 0 && (
        <Box>
          {dashLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Grid container spacing={3}>
              {statCards.map((card) => (
                <Grid item xs={6} sm={4} md={2.4} key={card.label}>
                  <Card sx={{ height: '100%', borderTop: `4px solid ${card.color}` }}>
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <card.icon sx={{ fontSize: 36, color: card.color, mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>{card.value}</Typography>
                      <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Tab 1: Usuários */}
      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              size="small"
              placeholder="Buscar por email ou nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchUsers(searchTerm); }}
              sx={{ flex: 1 }}
            />
            <Button variant="contained" onClick={() => fetchUsers(searchTerm)}>Buscar</Button>
          </Box>
          {usersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Plano</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Cadastro</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.name || '-'}</TableCell>
                      <TableCell><Chip label={user.plan || 'free'} size="small" /></TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_active ? 'Ativo' : 'Inativo'}
                          size="small"
                          color={user.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{new Date(user.created_at).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Button size="small" variant="outlined" onClick={() => openConfirm('Reset Senha', `Resetar senha de ${user.email}?`, () => handleUserAction('reset-password', user.id))}>
                            Reset Senha
                          </Button>
                          <Button size="small" variant="outlined" onClick={() => openConfirm('Desbloquear', `Desbloquear ${user.email}?`, () => handleUserAction('unlock', user.id))}>
                            Desbloquear
                          </Button>
                          <Button size="small" variant="outlined" color={user.is_active ? 'error' : 'success'} onClick={() => openConfirm(user.is_active ? 'Desativar' : 'Ativar', `${user.is_active ? 'Desativar' : 'Ativar'} ${user.email}?`, () => handleUserAction('toggle-active', user.id))}>
                            {user.is_active ? 'Desativar' : 'Ativar'}
                          </Button>
                          <Button size="small" variant="outlined" onClick={() => { setPlanDialog({ open: true, userId: user.id, currentPlan: user.plan || 'free' }); setNewPlan(user.plan || 'free'); }}>
                            Alterar Plano
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {users.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center">Nenhum usuário encontrado</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Tab 2: Observabilidade */}
      {tab === 2 && (
        <ObservabilityTab loading={obsLoading} data={obsData} onRefresh={fetchObservability} />
      )}

      {/* Tab 3: Auditoria */}
      {tab === 3 && (
        <Box>
          {auditLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Admin</TableCell>
                      <TableCell>Ação</TableCell>
                      <TableCell>Recurso</TableCell>
                      <TableCell>Detalhes</TableCell>
                      <TableCell>IP</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditLog.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{new Date(entry.timestamp).toLocaleString('pt-BR')}</TableCell>
                        <TableCell>{entry.admin_email}</TableCell>
                        <TableCell><Chip label={entry.action} size="small" /></TableCell>
                        <TableCell>{entry.resource}</TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.details}</TableCell>
                        <TableCell>{entry.ip}</TableCell>
                      </TableRow>
                    ))}
                    {auditLog.length === 0 && (
                      <TableRow><TableCell colSpan={6} align="center">Nenhum registro de auditoria</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button disabled={auditPage <= 1} onClick={() => { setAuditPage((p) => p - 1); fetchAuditLog(auditPage - 1); }}>Anterior</Button>
                <Typography variant="body2" sx={{ alignSelf: 'center' }}>Página {auditPage}</Typography>
                <Button disabled={auditLog.length < 20} onClick={() => { setAuditPage((p) => p + 1); fetchAuditLog(auditPage + 1); }}>Próxima</Button>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent><Typography>{confirmDialog.message}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={() => { confirmDialog.onConfirm(); setConfirmDialog({ ...confirmDialog, open: false }); }}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      {/* Plan Change Dialog */}
      <Dialog open={planDialog.open} onClose={() => setPlanDialog({ ...planDialog, open: false })}>
        <DialogTitle>Alterar Plano</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Plano atual: <strong>{planDialog.currentPlan}</strong></Typography>
          <TextField select fullWidth size="small" label="Novo Plano" value={newPlan} onChange={(e) => setNewPlan(e.target.value)} SelectProps={{ native: true }}>
            <option value="free">Free</option>
            <option value="explorer">Explorer</option>
            <option value="pro">Pro</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog({ ...planDialog, open: false })}>Cancelar</Button>
          <Button variant="contained" onClick={() => { handleUserAction('change-plan', planDialog.userId, { plan: newPlan }); setPlanDialog({ ...planDialog, open: false }); }}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

// Observability Tab Component
function ObservabilityTab({ loading, data, onRefresh }: { loading: boolean; data: ObservabilityData | null; onRefresh: () => void }) {
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  if (!data) {
    return <Alert severity="info">Sem dados de observabilidade disponíveis</Alert>;
  }

  const { system, endpoints, requestsPerHour, recentErrors, business } = data;
  const maxHourCount = Math.max(...requestsPerHour.map((r) => r.count), 1);

  // Determine system status
  let systemStatus: 'healthy' | 'degraded' | 'critical' = 'healthy';
  let statusColor = '#2e7d32';
  if (system.dbStatus === 'error') {
    systemStatus = 'critical';
    statusColor = '#d32f2f';
  } else if (system.errorRate > 10) {
    systemStatus = 'degraded';
    statusColor = '#f57c00';
  }

  const statusLabel = systemStatus === 'healthy' ? 'Saudável' : systemStatus === 'degraded' ? 'Degradado' : 'Crítico';

  // Sum business metrics
  const totalPins7d = business.pinsPerDay.reduce((sum, d) => sum + (d.count || 0), 0);
  const totalPhotos7d = business.photosPerDay.reduce((sum, d) => sum + (d.count || 0), 0);
  const totalRegistrations7d = business.registrationsPerDay.reduce((sum, d) => sum + (d.count || 0), 0);

  return (
    <Box>
      {/* Refresh Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="outlined" size="small" onClick={onRefresh}>Atualizar</Button>
      </Box>

      {/* Section 1: System Health */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Saúde do Sistema</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ height: '100%', borderTop: `4px solid ${statusColor}` }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: statusColor, mx: 'auto', mb: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 700 }}>{statusLabel}</Typography>
              <Typography variant="caption" color="text.secondary">Status</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{system.dbLatencyMs}</Typography>
              <Typography variant="caption" color="text.secondary">DB Latência (ms)</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{system.memory.heapUsed}</Typography>
              <Typography variant="caption" color="text.secondary">Heap (MB) / {system.memory.rss} RSS</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{formatUptime(system.uptimeSeconds)}</Typography>
              <Typography variant="caption" color="text.secondary">Uptime</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{system.requestsPerMinute}</Typography>
              <Typography variant="caption" color="text.secondary">Requests/min</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: system.errorRate > 5 ? '#d32f2f' : 'inherit' }}>{system.errorRate}%</Typography>
              <Typography variant="caption" color="text.secondary">Taxa de Erros</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Section 2: Requests last 24h bar chart */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Requests últimas 24h</Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 100 }}>
            {requestsPerHour.map((item) => (
              <Box key={item.hour} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: '0.55rem', mb: 0.5, color: 'text.secondary' }}>{item.count || ''}</Typography>
                <Box sx={{ width: '100%', height: `${(item.count / maxHourCount) * 100}%`, bgcolor: 'primary.main', borderRadius: '2px 2px 0 0', minHeight: 2 }} />
                <Typography variant="caption" sx={{ fontSize: '0.6rem', mt: 0.5 }}>{item.hour.split(':')[0]}</Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Section 3: Top Endpoints */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Top Endpoints</Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Path</TableCell>
              <TableCell align="right">Requests</TableCell>
              <TableCell align="right">Latência Média (ms)</TableCell>
              <TableCell align="right">Erros</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {endpoints.map((ep) => (
              <TableRow key={ep.path}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{ep.path}</TableCell>
                <TableCell align="right">{ep.count}</TableCell>
                <TableCell align="right">{ep.avgLatency}</TableCell>
                <TableCell align="right" sx={{ color: ep.errorCount > 0 ? '#d32f2f' : 'inherit' }}>{ep.errorCount}</TableCell>
              </TableRow>
            ))}
            {endpoints.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center">Nenhum dado disponível</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Section 4: Business Metrics */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Métricas de Negócio</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{business.activeUsers24h}</Typography>
              <Typography variant="caption" color="text.secondary">Usuários ativos 24h</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{business.activeUsers7d}</Typography>
              <Typography variant="caption" color="text.secondary">Usuários ativos 7d</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center' }}>{totalPins7d}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>Pins criados (7d)</Typography>
              <MiniBarChart data={business.pinsPerDay} color="#d32f2f" />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center' }}>{totalPhotos7d}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>Fotos enviadas (7d)</Typography>
              <MiniBarChart data={business.photosPerDay} color="#2e7d32" />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center' }}>{totalRegistrations7d}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>Registros (7d)</Typography>
              <MiniBarChart data={business.registrationsPerDay} color="#1565c0" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Section 5: Recent Errors */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Erros Recentes</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>Path</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Mensagem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recentErrors.slice(0, 20).map((err, idx) => (
              <TableRow key={idx} sx={{ bgcolor: err.statusCode >= 500 ? 'rgba(211, 47, 47, 0.08)' : undefined }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(err.timestamp).toLocaleString('pt-BR')}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{err.path}</TableCell>
                <TableCell>
                  <Chip label={err.statusCode} size="small" color={err.statusCode >= 500 ? 'error' : 'warning'} />
                </TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{err.message}</TableCell>
              </TableRow>
            ))}
            {recentErrors.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center">Nenhum erro recente</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// Mini bar chart for business metrics
function MiniBarChart({ data, color }: { data: { day?: string; count: number }[]; color: string }) {
  const maxVal = Math.max(...data.map((d) => d.count || 0), 1);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 30 }}>
      {data.map((item, idx) => (
        <Box key={idx} sx={{ flex: 1 }}>
          <Box sx={{ width: '100%', height: `${((item.count || 0) / maxVal) * 100}%`, bgcolor: color, borderRadius: '1px 1px 0 0', minHeight: 2, opacity: 0.8 }} />
        </Box>
      ))}
    </Box>
  );
}